import { NotificationEnum } from "@graphql/generated/graphql.g";
import {
  NotificationFragment,
  useNotificationsQuery,
} from "@graphql/notifications/Notifications.g";
import {
  ColumnDef,
  PaginationState,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { useTableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/useTableColumnSettingsProvider";

import { Box } from "@design-system/Box/Box";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Table } from "@design-system/Table/Table";

import { routes } from "@pages/routes";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";
import { ConfigurableTableSystem } from "@utils/table/TableConfigurationSystem";

import { ClientApplicationReviewPanel } from "./ClientApplicationReviewPanel/ClientApplicationReviewPanel";

import {
  ClientNotificationsTableConfig,
  renderApplicationName,
  renderNotificationType,
  renderCreatedAt,
} from "./ClientNotificationsTableConfig";
import { ClientNotificationTableColumns } from "./UI/ClientNotificationsTableColumns";

const PAGE_SIZE = 30;

const columnHelper = createColumnHelper<NotificationFragment>();

type Props = {
  initialPageIndex?: number;
  clientId: string;
  // Filter parameters
  notificationType?: NotificationEnum[];
  applicationNameSearch?: string;
};

export const ClientNotificationsTable = ({
  initialPageIndex = 0,
  clientId,
  notificationType,
  applicationNameSearch,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: PAGE_SIZE,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedNotificationForReview, setSelectedNotificationForReview] = useState<NotificationFragment | null>(null);

  const { columnSettings, columnOrder } = useTableColumnSettingsProvider();

  const notificationsQuery = useNotificationsQuery({
    clientId,
    first: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    resolvedAt_Isnull: true,
    sortBy: ConfigurableTableSystem.generateSortParameter(
      ClientNotificationsTableConfig.columns,
      sorting,
    ),
    notificationType_In:
      notificationType && notificationType.length > 0
        ? notificationType
        : undefined,
    application_Resource_ProductName_Icontains:
      applicationNameSearch && applicationNameSearch.trim()
        ? applicationNameSearch.trim()
        : undefined,
  });

  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );

  const totalCount =
    notificationsQuery.data?.notifications?.totalCount ?? undefined;

  const data = useMemo(() => {
    return (
      notificationsQuery.data?.notifications?.edges
        .map((edge) => edge?.node)
        .filter((notification) => !!notification) ?? []
    );
  }, [notificationsQuery.data]);

  const handleDetailsClick = useCallback(
    (clientId: string, applicationId: string) => {
      navigate(
        routes.portal.clients.client.applications.application.$buildPath({
          params: { clientId, applicationId },
        }),
        {
          viewTransition: true,
        },
      );
    },
    [navigate],
  );

  const handleNotificationClick = useCallback(
    (notificationId: string) => {
      setSearchParams((prev) => {
        prev.set("notificationId", notificationId);
        return prev;
      });
    },
    [setSearchParams],
  );

  const handleReviewClick = useCallback((notification: NotificationFragment) => {
    setSelectedNotificationForReview(notification);
  }, []);

  const handleCloseReviewPanel = useCallback(() => {
    setSelectedNotificationForReview(null);
  }, []);

  const handleReviewSuccess = useCallback(() => {
    notificationsQuery.refetch();
  }, [notificationsQuery]);

  const configuredColumns = useMemo(() => {
    // Create column configuration with custom renderers
    const configWithRenderers = {
      ...ClientNotificationsTableConfig,
      columns: Object.fromEntries(
        Object.entries(ClientNotificationsTableConfig.columns).map(
          ([key, config]) => [
            key,
            {
              ...config,
              render: (value: unknown, row: NotificationFragment) => {
                const columnKey = key as ClientNotificationTableColumns;

                // Map to the appropriate render function
                switch (columnKey) {
                  case ClientNotificationTableColumns.applicationName:
                    return renderApplicationName(
                      value,
                      row,
                      handleDetailsClick,
                    );
                  case ClientNotificationTableColumns.notificationType:
                    return renderNotificationType(
                      value,
                      row,
                      handleNotificationClick,
                    );
                  case ClientNotificationTableColumns.createdAt:
                    return renderCreatedAt(value);
                  default:
                    return String(value);
                }
              },
            },
          ],
        ),
      ) as typeof ClientNotificationsTableConfig.columns,
    };

    return ConfigurableTableSystem.generateColumnsFromConfig(
      data,
      configWithRenderers,
      {
        columnOrder:
          columnOrder.length > 0
            ? (columnOrder as ClientNotificationTableColumns[])
            : undefined,
        columnVisibility: columnSettings,
      },
    );
  }, [data, columnOrder, columnSettings, handleDetailsClick, handleNotificationClick]);

  const allColumnsWithActions = useMemo(() => {
    const columns: ColumnDef<NotificationFragment, unknown>[] = [
      ...configuredColumns,
    ];

    // Always add the actions column at the end
    columns.push(
      columnHelper.display({
        id: "actions",
        header: "",
        meta: {
          sticky: true,
          actions: true,
        },
        cell: (props) => {
          const notification = props.row.original;
          const isSaasDiscovered = notification.notificationType === NotificationEnum.SaasDiscovered;
          
          return (
            <Box gap={2}>
              <Dropdown
                trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
              >
                <Dropdown.Option
                  label="Details"
                  icon="faFileLines"
                  onSelect={() => handleNotificationClick(notification.id)}
                />
                {isSaasDiscovered && (
                  <Dropdown.Option
                    label="Review"
                    icon="faPencil"
                    onSelect={() => handleReviewClick(notification)}
                  />
                )}
              </Dropdown>
            </Box>
          );
        },
      }),
    );

    return columns;
  }, [
    configuredColumns,
    handleNotificationClick,
    handleReviewClick,
  ]);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <>
      <Table<NotificationFragment>
        columns={allColumnsWithActions as ColumnDef<NotificationFragment>[]}
        data={data}
        rowCount={totalCount}
        onPaginationChange={handlePaginationChange}
        pagination={pagination}
        isLoading={notificationsQuery.isLoading}
        isFetching={notificationsQuery.isFetching}
        onPageSizeChange={handlePageSizeChange}
        sorting={sorting}
        onSortingChange={setSorting}
        columnVisibility={columnSettings}
      />

      <ClientApplicationReviewPanel
        notification={selectedNotificationForReview}
        isOpen={!!selectedNotificationForReview}
        onClose={handleCloseReviewPanel}
        onSuccess={handleReviewSuccess}
      />
    </>
  );
};
